import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { BaseAutomationDiscoveryDialog } from '../BaseAutomationDiscoveryDialog.tsx';
import { EventSourceName } from '../utils/analytics.ts';

export const i18n = defineMessages({
	feedbackAnalysisModalHeader: {
		id: 'automation-discovery.rovo.feedback-analysis.modal.header',
		defaultMessage: '{publishedPageTitle} was successfully published in the {spaceName} space',
		description:
			'This text is displayed as a title in a modal that says the page was published in the specified space',
	},
	feedbackAnalysisCta: {
		id: 'automation-discovery.rovo.feedback-analysis.modal.cta',
		defaultMessage: 'Automate your feedback analysis with a template?',
		description:
			'This text is displayed in a modal to ask the user if they want to automatically create a feedback analysis automation rule',
	},
	feedbackKeywordForModalTrigger: {
		id: 'automation-discovery.rovo.feedback-analysis.feedback.trigger.keyword',
		defaultMessage: 'feedback',
		description: 'Keyword in page title that triggers automation discovery dialog',
	},
	summaryKeywordForModalTrigger: {
		id: 'automation-discovery.rovo.feedback-analysis.summary.trigger.keyword',
		defaultMessage: 'summary',
		description: 'Keyword in page title that triggers automation discovery dialog',
	},
	analysisKeywordForModalTrigger: {
		id: 'automation-discovery.rovo.feedback-analysis.analysis.trigger.keyword',
		defaultMessage: 'analysis',
		description: 'Keyword in page title that triggers automation discovery dialog',
	},
});

type Intl = {
	formatMessage: (messageDescriptor: { id: string; defaultMessage: string }) => string;
};

export const isEligibleForFeedbackAnalysisDiscovery = (pageTitle: string, intl: Intl): boolean => {
	// show the dialog if title contains "analysis" or "feedback summary"
	return (
		pageTitle.toLowerCase().includes(intl.formatMessage(i18n.analysisKeywordForModalTrigger)) ||
		(pageTitle.toLowerCase().includes(intl.formatMessage(i18n.feedbackKeywordForModalTrigger)) &&
			pageTitle.toLowerCase().includes(intl.formatMessage(i18n.summaryKeywordForModalTrigger)))
	);
};

type FeedbackAnalysisDialogProps = {
	onClose: () => void;
	onCloseComplete: () => void;
	publishedPageTitle: string;
	spaceName: string;
	spaceKey?: string;
};

const FeedbackAnalysisDialogComponent = ({
	onClose,
	publishedPageTitle,
	spaceName,
	spaceKey,
}: FeedbackAnalysisDialogProps) => {
	return (
		<BaseAutomationDiscoveryDialog
			eventSourceName={EventSourceName.FEEDBACK_ANALYSIS}
			templateName="createReleaseNotesPageAfterJiraIssueCreation"
			discoveryExperience="automationDiscoveryFeedbackAnalysis"
			onClose={onClose}
			cta={<FormattedMessage {...i18n.feedbackAnalysisCta} />}
			header={
				<FormattedMessage
					{...i18n.feedbackAnalysisModalHeader}
					values={{ publishedPageTitle, spaceName }}
				/>
			}
			localStorageKey={keys.AUTOMATION_DISCOVER_FEEDBACK_ANALYSIS}
			templateUrl={`/wiki/spaces/${spaceKey}/settings/automation#/rule/template?templateId=confluence_rovo_template_3`}
			spaceKey={spaceKey}
		/>
	);
};

export const FeedbackAnalysisDialog = withTransparentErrorBoundary({
	attribution: Attribution.AUTOMATION_FOR_CONFLUENCE,
})(FeedbackAnalysisDialogComponent);
